import Title from "common/Typography/Title";
import { useMemo } from "react";
import PieChart from "common/Charts/PieChart/PieChart";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Tag from "common/Tags/Tag";
import styled, { useTheme } from "styled-components";
import {
  BacktestingStatisticCard,
  NewLookStatisticTitle,
} from "features/bots/BotForm/common/styles";
import AllocationDonut from "common/NewLook/AllocationDonut";
import EmptyView from "common/Empty/EmptyView";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { usePnlStatusStatistic } from "@shared/features/bots/BotForm/Backtesting/hooks";
import { BACKTESTING_PNL_STATUS_TO_COLOR } from "@shared/features/bots/BotForm/common/constants";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { BacktestingPnlStatus } from "@shared/features/bots/BotForm/common/types";
import { getNewLookTokens, NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { hexToRGB } from "@shared/utils/misc";

const PnlStatusStatistic = () => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const theme = useTheme();
  const { pieChartData, isEmpty, helperProps } = usePnlStatusStatistic();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const legendPieChartData = useMemo(
    () =>
      pieChartData.map((dataPoint) => ({
        ...dataPoint,
        legendDisplay: (
          <Flex $justifyContent="flex-start">
            <StyledText>
              {dataPoint.name
                .charAt(0)
                .toUpperCase()
                .concat(dataPoint.name.slice(1))}
              :
            </StyledText>
            <StyledText style={{ margin: "0 4px", whiteSpace: "nowrap" }}>
              {t("backtesting.statistics.num_trades", {
                num: dataPoint.originalValue,
              })}
            </StyledText>
            <Tag
              color={
                theme.colors[BACKTESTING_PNL_STATUS_TO_COLOR[dataPoint.name]]
              }
              style={{ padding: "2px 4px" }}
            >
              {dataPoint.value}%
            </Tag>
          </Flex>
        ),
      })),
    [pieChartData]
  );

  const newLookData = useMemo(
    () =>
      pieChartData.map((dataPoint) => {
        const name = dataPoint.name
          ? dataPoint.name.charAt(0).toUpperCase() + dataPoint.name.slice(1)
          : "";
        const color =
          (theme.colors[
            BACKTESTING_PNL_STATUS_TO_COLOR[
              dataPoint.name as keyof typeof BACKTESTING_PNL_STATUS_TO_COLOR
            ]
          ] as string | undefined) ?? dataPoint.color;
        return {
          ...dataPoint,
          name,
          color,
          // Yellow text does not read on a light chip: neutral uses orange.
          chipColor:
            dataPoint.name === BacktestingPnlStatus.NEUTRAL
              ? theme.colors.orange
              : color,
        };
      }),
    [pieChartData, theme, t]
  );

  if (theme.isNewLook) {
    const totalTrades = pieChartData.reduce(
      (sum, dataPoint) => sum + (dataPoint.originalValue ?? 0),
      0
    );
    return (
      <BacktestingStatisticCard
        style={{ gridArea: "PnlStatus" }}
        $isMobile={isMobile}
      >
        <NewLookStatisticTitle>
          {t("backtesting.statistics.pnl_status")}
          <HelperTooltip {...helperProps} />
        </NewLookStatisticTitle>
        {!isEmpty ? (
          <NewLookBody>
            <NewLookLegend>
              {newLookData.map((dataPoint) => (
                <li key={dataPoint.name}>
                  <i style={{ background: dataPoint.color }} />
                  <span>{dataPoint.name}</span>
                  <MutedText>
                    {t("backtesting.statistics.num_trades", {
                      num: dataPoint.originalValue,
                    })}
                  </MutedText>
                  <Chip
                    style={{
                      color: dataPoint.chipColor,
                      background: hexToRGB(
                        dataPoint.chipColor,
                        theme.isDarkMode ? 0.16 : 0.1
                      ),
                    }}
                  >
                    {dataPoint.value}%
                  </Chip>
                </li>
              ))}
            </NewLookLegend>
            <AllocationDonut
              bare={true}
              compact={true}
              hideLegend={true}
              chartId="tradesStatusDonut"
              data={newLookData}
              total={totalTrades}
            />
          </NewLookBody>
        ) : (
          <EmptyView
            iconKey={EmptyStatesType.Trades}
            title={t("empty_states.no_status")}
            iconWidth={64}
          />
        )}
      </BacktestingStatisticCard>
    );
  }

  return (
    <BacktestingStatisticCard
      style={{ gridArea: "PnlStatus" }}
      $isMobile={isMobile}
    >
      <Title type="secondary" level={3}>
        {t("backtesting.statistics.pnl_status")}
        <HelperTooltip {...helperProps} />
      </Title>
      {!isEmpty ? (
        <PieChart
          data={legendPieChartData}
          horizontal={true}
          extraInfoOnTheLeft={true}
          hideTotal={true}
          numItemsInRow={1}
          numRows={3}
          chartSize={isMobile ? 120 : 160}
          chartWeight={isMobile ? "84%" : "88%"}
          chartParentElementId="tradesStatusPieChart"
          tooltipElementId="tradesStatusTooltip"
          extraInfoStyle={EXTRA_INFO_STYLE}
          chartStyle={CHART_STYLE}
          hideTooltip={isMobile}
          disableHover={isMobile}
        />
      ) : (
        <EmptyView
          iconKey={EmptyStatesType.Trades}
          title={t("empty_states.no_status")}
          iconWidth={80}
        />
      )}
    </BacktestingStatisticCard>
  );
};

export default PnlStatusStatistic;

const CHART_STYLE = { margin: 0, flex: "none" };
const EXTRA_INFO_STYLE = { margin: 0 };

const StyledText = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
const MutedText = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).muted};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const NewLookBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
`;
// Dot, name, trade count and share as one grid, so the columns line up.
const NewLookLegend = styled.ul`
  flex: 0 1 auto;
  display: grid;
  grid-template-columns: 6px max-content max-content max-content;
  align-items: center;
  gap: 12px 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: contents;
  }
  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  li > span:nth-of-type(1) {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => getNewLookTokens(p.theme).ink};
    white-space: nowrap;
  }
`;
const Chip = styled.span`
  justify-self: end;
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
